import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compact, get } from 'lodash-es';
import Row from 'reactstrap/lib/Row';
import classNames from 'classnames';
import { Link } from 'site-modules/shared/components/link/link';
import { InventoryEntities } from 'client/data/models/inventory';
import { getStaticImageUrl } from 'client/utils/image-helpers';
import { isPrivateParty } from 'site-modules/shared/utils/dealer-details-utils';
import { ContentSection } from 'site-modules/shared/components/content-section/content-section';
import { isNew } from 'site-modules/shared/utils/inventory-utils/is-new';
import {
  getCallToAction,
  getHistoryStatus,
  getHistoryPoints,
  AUTOCHECK_LOGO_URL,
} from 'site-modules/shared/components/inventory/helpers/vehicle-history-helper';
import { VehicleEntities } from 'client/data/models/vehicle';

import { VehicleHistoryItem } from './vehicle-history-item';

import './vehicle-history.scss';

/**
 * Shows vehicle history icons with description and CTA
 * @param {object} vinData
 * @param {boolean} [isVlaPage=false]
 * @param {object} vehicle
 * @param {string} token
 * @returns {ReactElement}
 */
function VehicleHistoryUI({ vinData = {}, isVlaPage = false, vehicle = null, token = '' }) {
  if (isNew(vinData.type)) {
    return null;
  }

  const { historyInfo = {}, vin, dealerInfo } = vinData;
  const { franchiseId } = dealerInfo;
  const { freeHistoryReport, historyProvider } = historyInfo;
  const isPrivatePartyListing = isPrivateParty(vinData);

  const historyDisplayStatus = getHistoryStatus(freeHistoryReport, historyProvider, isPrivatePartyListing);
  const cta = getCallToAction({
    historyDisplayStatus,
    vin,
    franchiseId,
    make: get(vehicle, 'make.slug', ''),
    model: get(vehicle, 'model.slug', ''),
    year: get(vehicle, 'year', ''),
    token,
    isPrivatePartyListing,
  });
  const historyPoints = compact(getHistoryPoints(historyProvider, historyInfo, true));
  const autocheckLogo = (
    <img
      className="ms-0_25 ms-sm-0_75"
      src={getStaticImageUrl(AUTOCHECK_LOGO_URL)}
      height={30}
      width={107}
      alt="AutoCheck"
      loading="lazy"
    />
  );

  const sectionTitle = isPrivatePartyListing ? (
    <Fragment>Vehicle History</Fragment>
  ) : (
    <Fragment>
      Vehicle History Report by
      {autocheckLogo}
    </Fragment>
  );

  return (
    <ContentSection
      dataTrackingParent="edm-entry-vehicle-history"
      titleClassName={classNames('section-title heading-4', isPrivatePartyListing ? 'mb-0_5' : 'mb-1_5')}
      line={false}
      headerTag="h3"
      sectionTitle={sectionTitle}
    >
      <div className="vehicle-history" data-id={vin}>
        {isPrivatePartyListing && <div className="mb-1_5">Learn about this car before buying with {autocheckLogo}</div>}
        <Row tag="ul" className="list-unstyled max-width mb-0_25 px-0">
          {historyPoints.map(point => (
            <VehicleHistoryItem key={`${point.name}-${point.content}`} {...point} />
          ))}
        </Row>

        {!isVlaPage && cta && (
          <div className="mt-1 mb-1_5 text-primary-darker">
            <Link
              data-tracking-id={cta.trackingId}
              target="_blank"
              to={cta.url}
              rel="nofollow"
              className={classNames({
                'px-1 py-0_75 btn btn-outline-blue-50 buy-report-btn size-16 text-underline-none':
                  isPrivatePartyListing,
              })}
            >
              {cta.title}
              <span className="mb-0 ms-0_75 icon-open_in_new" aria-hidden />
            </Link>
          </div>
        )}
        {!isVlaPage && (
          <div className="text-primary-darker">
            <Link
              data-tracking-id="see_recall_info"
              target="_blank"
              to={`//www.nhtsa.gov/recalls?vin=${vin}`}
              rel="nofollow"
            >
              Visit NHTSA for recall information
              <span className="mb-0 ms-0_75 icon-open_in_new" aria-hidden />
            </Link>
          </div>
        )}
      </div>
    </ContentSection>
  );
}

VehicleHistoryUI.propTypes = {
  vinData: InventoryEntities.InventoryVin,
  isVlaPage: PropTypes.bool,
  vehicle: VehicleEntities.MakeModelSubmodelYear,
  token: PropTypes.string,
};

export const mapStateToProps = state => ({
  token: get(state, 'pageContext.page.vehicleHistoryToken', ''),
});

export const VehicleHistory = connect(mapStateToProps)(VehicleHistoryUI);
